import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import {
  Shield,
  Bug,
  Key,
  Database,
  Flame,
  Play,
  Copy,
  Edit3,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Search,
  Filter,
  MoreVertical,
  ChevronRight,
  Zap,
  Calendar,
  Timer,
  Activity,
  Eye,
  X,
  Loader2,
  Save,
  User,
  Tag,
  RefreshCw,
} from 'lucide-react';
import './playbook.scss';

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const TEMPLATE_STEPS = {
  'Phishing Investigation': [
    'Extract and parse email headers',
    'Analyze sender reputation via threat intel',
    'Scan embedded URLs for malicious content',
    'Inspect and sandbox email attachments',
    'Cross-reference IOCs against threat database',
    'Notify assigned SOC analyst',
    'Auto-create incident ticket in SIEM',
    'Generate full investigation report',
  ],
  'Malware Alert': [
    'Detect and log malicious process tree',
    'Isolate affected endpoint from network',
    'Collect forensic artifacts and memory dump',
    'Submit sample to sandbox for analysis',
    'Extract IOCs from sandbox results',
    'Block file hash across all endpoints',
    'Notify incident response analyst',
    'Push IOCs to EDR blocklist',
    'Update SIEM detection rules',
    'Verify endpoint clean state',
    'Close containment window',
    'Archive forensic evidence',
  ],
  'Brute Force Login': [
    'Detect repeated failed authentication events',
    'Block source IP at perimeter firewall',
    'Temporarily lock targeted account',
    'Notify account owner via email',
    'Enforce MFA re-validation on next login',
    'Log incident and generate summary report',
  ],
  'Data Exfiltration': [
    'Monitor outbound traffic for anomalies',
    'Trigger DLP policy enforcement',
    'Revoke active access tokens for affected user',
    'Block suspicious upload destination',
    'Preserve full egress logs for audit',
    'Alert compliance and legal team',
    'Notify data owner and IT security',
    'Review and restrict access permissions',
    'Update DLP rules based on findings',
    'Formally close and document the case',
  ],
  'Firewall IPS Investigation': [
    'Parse and normalize IPS alert data',
    'Correlate with recent network flow events',
    'Enrich source IP with threat intelligence',
    'Check associated CVEs for exploitability',
    'Update firewall rule to block source',
    'Notify network operations center (NOC)',
    'Document finding in asset register',
    'Review and tune IPS policy',
    'Generate investigation report',
  ],
};

const TEMPLATES = [
  {
    id: 'tpl-1',
    name: 'Phishing Investigation',
    description:
      'Automated investigation workflow for suspected phishing emails. Analyzes headers, URLs, attachments and generates a full incident report.',
    icon: Shield,
    category: 'Email Security',
    steps: 8,
    avgDuration: '4m 30s',
    tags: ['email', 'phishing', 'investigation'],
    color: '#0ea5e9',
  },
  {
    id: 'tpl-2',
    name: 'Malware Alert',
    description:
      'Rapid response playbook for malware detections. Isolates affected endpoints, collects forensic artifacts and initiates remediation steps.',
    icon: Bug,
    category: 'Endpoint Security',
    steps: 12,
    avgDuration: '7m 15s',
    tags: ['malware', 'endpoint', 'containment'],
    color: '#ef4444',
  },
  {
    id: 'tpl-3',
    name: 'Brute Force Login',
    description:
      'Detects and responds to brute-force login attempts. Blocks offending IPs, notifies account owners and enforces MFA revalidation.',
    icon: Key,
    category: 'Identity & Access',
    steps: 6,
    avgDuration: '2m 45s',
    tags: ['brute-force', 'identity', 'access'],
    color: '#f59e0b',
  },
  {
    id: 'tpl-4',
    name: 'Data Exfiltration',
    description:
      'Monitors and responds to suspected data exfiltration events. Triggers DLP rules, revokes access tokens and preserves audit logs.',
    icon: Database,
    category: 'Data Protection',
    steps: 10,
    avgDuration: '6m 00s',
    tags: ['dlp', 'data', 'exfiltration'],
    color: '#a855f7',
  },
  {
    id: 'tpl-5',
    name: 'Firewall IPS Investigation',
    description:
      'Investigates IPS/Firewall alerts for potential intrusion attempts. Correlates network events, enriches with threat intel and recommends rule updates.',
    icon: Flame,
    category: 'Network Security',
    steps: 9,
    avgDuration: '5m 20s',
    tags: ['firewall', 'ips', 'network'],
    color: '#39ff14',
  },
];

const INITIAL_PLAYBOOKS = [
  {
    id: 'pb-1',
    name: 'Phishing Response – Patel Brothers',
    template: 'Phishing Investigation',
    status: 'active',
    lastRun: '2026-05-11T08:14:00Z',
    nextRun: '2026-05-11T20:00:00Z',
    totalRuns: 47,
    successRate: 95,
    trigger: 'Automated',
    owner: 'Hardik Baraiya',
    description: 'Automated phishing triage and response for the Patel Brothers client tenant.',
  },
  {
    id: 'pb-2',
    name: 'Endpoint Malware Containment',
    template: 'Malware Alert',
    status: 'active',
    lastRun: '2026-05-10T14:30:00Z',
    nextRun: null,
    totalRuns: 23,
    successRate: 87,
    trigger: 'Alert-triggered',
    owner: 'Hardik Baraiya',
    description: 'Automatically contains and remediates malware-infected endpoints upon EDR alert.',
  },
  {
    id: 'pb-3',
    name: 'Identity Breach Drill',
    template: 'Brute Force Login',
    status: 'draft',
    lastRun: null,
    nextRun: null,
    totalRuns: 0,
    successRate: null,
    trigger: 'Manual',
    owner: 'Hardik Baraiya',
    description: 'Drill playbook to test identity breach response procedures.',
  },
  {
    id: 'pb-4',
    name: 'DLP Weekly Scan',
    template: 'Data Exfiltration',
    status: 'draft',
    lastRun: null,
    nextRun: '2026-05-18T09:00:00Z',
    totalRuns: 0,
    successRate: null,
    trigger: 'Scheduled',
    owner: 'Hardik Baraiya',
    description: 'Weekly DLP scan playbook that monitors for data exfiltration patterns.',
  },
  {
    id: 'pb-5',
    name: 'Legacy Firewall Audit',
    template: 'Firewall IPS Investigation',
    status: 'disabled',
    lastRun: '2026-03-22T11:00:00Z',
    nextRun: null,
    totalRuns: 12,
    successRate: 75,
    trigger: 'Scheduled',
    owner: 'Hardik Baraiya',
    description: 'Periodic audit of legacy firewall IPS alerts. Temporarily disabled pending upgrade.',
  },
];

const EXECUTION_HISTORY = [
  {
    id: 'ex-1',
    playbookName: 'Phishing Response – Patel Brothers',
    trigger: 'Automated',
    status: 'successful',
    startTime: '2026-05-11T08:14:00Z',
    duration: '4m 12s',
    stepsCompleted: '8/8',
    initiatedBy: 'System',
  },
  {
    id: 'ex-2',
    playbookName: 'Endpoint Malware Containment',
    trigger: 'Alert-triggered',
    status: 'successful',
    startTime: '2026-05-10T14:30:00Z',
    duration: '7m 08s',
    stepsCompleted: '12/12',
    initiatedBy: 'CrowdStrike Alert',
  },
  {
    id: 'ex-3',
    playbookName: 'Phishing Response – Patel Brothers',
    trigger: 'Automated',
    status: 'failed',
    startTime: '2026-05-10T02:11:00Z',
    duration: '1m 45s',
    stepsCompleted: '3/8',
    initiatedBy: 'System',
  },
  {
    id: 'ex-4',
    playbookName: 'DLP Weekly Scan',
    trigger: 'Manual',
    status: 'waiting_approval',
    startTime: '2026-05-09T16:50:00Z',
    duration: '—',
    stepsCompleted: '5/10',
    initiatedBy: 'Hardik Baraiya',
  },
  {
    id: 'ex-5',
    playbookName: 'Endpoint Malware Containment',
    trigger: 'Alert-triggered',
    status: 'successful',
    startTime: '2026-05-09T10:05:00Z',
    duration: '6m 55s',
    stepsCompleted: '12/12',
    initiatedBy: 'Microsoft Defender',
  },
  {
    id: 'ex-6',
    playbookName: 'Legacy Firewall Audit',
    trigger: 'Scheduled',
    status: 'failed',
    startTime: '2026-05-08T09:00:00Z',
    duration: '2m 30s',
    stepsCompleted: '4/9',
    initiatedBy: 'System',
  },
  {
    id: 'ex-7',
    playbookName: 'Phishing Response – Patel Brothers',
    trigger: 'Automated',
    status: 'waiting_approval',
    startTime: '2026-05-07T22:44:00Z',
    duration: '—',
    stepsCompleted: '6/8',
    initiatedBy: 'System',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusBadge({ status }) {
  const map = {
    active: { label: 'Active', cls: 'status--active' },
    draft: { label: 'Draft', cls: 'status--draft' },
    disabled: { label: 'Disabled', cls: 'status--disabled' },
    successful: { label: 'Successful', cls: 'status--successful' },
    failed: { label: 'Failed', cls: 'status--failed' },
    waiting_approval: { label: 'Waiting Approval', cls: 'status--waiting' },
  };
  const s = map[status] || { label: status, cls: '' };
  return <span className={`pb-badge ${s.cls}`}>{s.label}</span>;
}

// ─── Modal Backdrop ───────────────────────────────────────────────────────────

function Modal({ title, onClose, children, size = 'md' }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="pb-modal-overlay" onClick={onClose}>
      <div
        className={`pb-modal pb-modal--${size}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pb-modal__header">
          <h2 className="pb-modal__title">{title}</h2>
          <button className="pb-modal__close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="pb-modal__body">{children}</div>
      </div>
    </div>
  );
}

// ─── Run Modal ────────────────────────────────────────────────────────────────

function RunModal({ playbook, onClose }) {
  const steps = TEMPLATE_STEPS[playbook.template] || ['Initializing…', 'Running…', 'Done'];
  const [completedCount, setCompletedCount] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCompletedCount((prev) => {
        if (prev >= steps.length) {
          clearInterval(intervalRef.current);
          setIsDone(true);
          return prev;
        }
        return prev + 1;
      });
    }, 900);
    return () => clearInterval(intervalRef.current);
  }, [steps.length]);

  const progress = Math.min((completedCount / steps.length) * 100, 100);

  return (
    <Modal
      title={isDone ? `✓ Run Complete` : `Running: ${playbook.name}`}
      onClose={onClose}
      size="md"
    >
      <div className="pb-run-modal">
        <div className="pb-run-modal__meta">
          <span className="pb-meta-chip"><Tag size={11} /> {playbook.template}</span>
          <span className="pb-meta-chip"><User size={11} /> {playbook.owner}</span>
          <span className="pb-meta-chip"><Zap size={11} /> {steps.length} steps</span>
        </div>

        <div className="pb-run-modal__progress-wrap">
          <div className="pb-run-modal__progress-bar">
            <div
              className={`pb-run-modal__progress-fill${isDone ? ' pb-run-modal__progress-fill--done' : ''}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="pb-run-modal__progress-label">
            {isDone ? '100%' : `${Math.round(progress)}%`}
          </span>
        </div>

        <div className="pb-run-modal__steps">
          {steps.map((step, i) => {
            const done = i < completedCount;
            const running = i === completedCount && !isDone;
            return (
              <div
                key={i}
                className={`pb-run-step${done ? ' pb-run-step--done' : ''}${running ? ' pb-run-step--running' : ''}`}
              >
                <div className="pb-run-step__icon">
                  {done ? (
                    <CheckCircle size={14} />
                  ) : running ? (
                    <Loader2 size={14} className="pb-spin" />
                  ) : (
                    <div className="pb-run-step__dot" />
                  )}
                </div>
                <span className="pb-run-step__label">{step}</span>
                {done && <span className="pb-run-step__time">✓</span>}
              </div>
            );
          })}
        </div>

        {isDone && (
          <div className="pb-run-modal__result">
            <CheckCircle size={18} />
            <span>All {steps.length} steps completed successfully.</span>
          </div>
        )}

        <div className="pb-run-modal__footer">
          {!isDone ? (
            <button
              className="pb-btn pb-btn--ghost"
              onClick={() => { clearInterval(intervalRef.current); onClose(); }}
            >
              Cancel
            </button>
          ) : (
            <button className="pb-btn pb-btn--primary" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── View Modal ───────────────────────────────────────────────────────────────

function ViewModal({ playbook, onClose }) {
  const steps = TEMPLATE_STEPS[playbook.template] || [];
  const recentRuns = EXECUTION_HISTORY.filter(
    (e) => e.playbookName === playbook.name
  ).slice(0, 3);

  return (
    <Modal title="Playbook Details" onClose={onClose} size="lg">
      <div className="pb-view-modal">
        <div className="pb-view-modal__top">
          <div className="pb-view-modal__name-row">
            <h3 className="pb-view-modal__name">{playbook.name}</h3>
            <StatusBadge status={playbook.status} />
          </div>
          {playbook.description && (
            <p className="pb-view-modal__desc">{playbook.description}</p>
          )}
          <div className="pb-view-modal__chips">
            <span className="pb-meta-chip"><ChevronRight size={11} />{playbook.template}</span>
            <span className="pb-meta-chip"><Zap size={11} />{playbook.trigger}</span>
            <span className="pb-meta-chip"><User size={11} />{playbook.owner}</span>
          </div>
        </div>

        <div className="pb-view-modal__stats">
          <div className="pb-view-stat">
            <span className="pb-view-stat__val">{playbook.totalRuns}</span>
            <span className="pb-view-stat__lbl">Total Runs</span>
          </div>
          <div className="pb-view-stat">
            <span className="pb-view-stat__val pb-view-stat__val--accent">
              {playbook.successRate !== null ? `${playbook.successRate}%` : '—'}
            </span>
            <span className="pb-view-stat__lbl">Success Rate</span>
          </div>
          <div className="pb-view-stat">
            <span className="pb-view-stat__val">{formatDate(playbook.lastRun)}</span>
            <span className="pb-view-stat__lbl">Last Run</span>
          </div>
          <div className="pb-view-stat">
            <span className="pb-view-stat__val">{formatDate(playbook.nextRun)}</span>
            <span className="pb-view-stat__lbl">Next Run</span>
          </div>
        </div>

        <div className="pb-view-modal__section">
          <h4 className="pb-view-modal__section-title">
            <Zap size={13} /> Execution Steps ({steps.length})
          </h4>
          <div className="pb-view-steps">
            {steps.map((step, i) => (
              <div key={i} className="pb-view-step">
                <span className="pb-view-step__num">{i + 1}</span>
                <span className="pb-view-step__label">{step}</span>
              </div>
            ))}
          </div>
        </div>

        {recentRuns.length > 0 && (
          <div className="pb-view-modal__section">
            <h4 className="pb-view-modal__section-title">
              <Clock size={13} /> Recent Executions
            </h4>
            <div className="pb-view-runs">
              {recentRuns.map((run) => (
                <div key={run.id} className="pb-view-run-row">
                  <StatusBadge status={run.status} />
                  <span className="pb-view-run-time">{formatDate(run.startTime)}</span>
                  <span className="pb-view-run-steps">{run.stepsCompleted} steps</span>
                  <span className="pb-view-run-dur">{run.duration}</span>
                  <span className="pb-view-run-by">{run.initiatedBy}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

function EditModal({ playbook, onSave, onClose }) {
  const [form, setForm] = useState({
    name: playbook.name,
    description: playbook.description || '',
    trigger: playbook.trigger,
    status: playbook.status,
    owner: playbook.owner,
  });
  const [saved, setSaved] = useState(false);

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    onSave({ ...playbook, ...form });
    setSaved(true);
    setTimeout(onClose, 900);
  };

  return (
    <Modal title="Edit Playbook" onClose={onClose} size="md">
      <div className="pb-edit-modal">
        {saved && (
          <div className="pb-edit-modal__saved">
            <CheckCircle size={14} /> Changes saved successfully
          </div>
        )}

        <div className="pb-form-field">
          <label className="pb-form-label">Playbook Name</label>
          <input
            className="pb-form-input"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
          />
        </div>

        <div className="pb-form-field">
          <label className="pb-form-label">Description</label>
          <textarea
            className="pb-form-input pb-form-textarea"
            value={form.description}
            rows={3}
            onChange={(e) => handleChange('description', e.target.value)}
          />
        </div>

        <div className="pb-form-row">
          <div className="pb-form-field">
            <label className="pb-form-label">Trigger</label>
            <select
              className="pb-form-input pb-form-select"
              value={form.trigger}
              onChange={(e) => handleChange('trigger', e.target.value)}
            >
              <option>Automated</option>
              <option>Alert-triggered</option>
              <option>Scheduled</option>
              <option>Manual</option>
            </select>
          </div>
          <div className="pb-form-field">
            <label className="pb-form-label">Status</label>
            <select
              className="pb-form-input pb-form-select"
              value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>

        <div className="pb-form-field">
          <label className="pb-form-label">Owner</label>
          <input
            className="pb-form-input"
            value={form.owner}
            onChange={(e) => handleChange('owner', e.target.value)}
          />
        </div>

        <div className="pb-form-field">
          <label className="pb-form-label">Template</label>
          <input
            className="pb-form-input"
            value={playbook.template}
            disabled
            style={{ opacity: 0.5, cursor: 'not-allowed' }}
          />
        </div>

        <div className="pb-edit-modal__footer">
          <button className="pb-btn pb-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="pb-btn pb-btn--primary" onClick={handleSave}>
            <Save size={13} />
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Execution View Modal ─────────────────────────────────────────────────────

function ExecViewModal({ execution, onClose }) {
  const steps = (() => {
    const pb = INITIAL_PLAYBOOKS.find((p) => p.name === execution.playbookName);
    return pb ? (TEMPLATE_STEPS[pb.template] || []) : [];
  })();
  const completedCount = execution.stepsCompleted
    ? parseInt(execution.stepsCompleted.split('/')[0], 10)
    : 0;

  return (
    <Modal title="Execution Details" onClose={onClose} size="md">
      <div className="pb-view-modal">
        <div className="pb-view-modal__top">
          <div className="pb-view-modal__name-row">
            <h3 className="pb-view-modal__name">{execution.playbookName}</h3>
            <StatusBadge status={execution.status} />
          </div>
          <div className="pb-view-modal__chips">
            <span className="pb-meta-chip"><Zap size={11} />{execution.trigger}</span>
            <span className="pb-meta-chip"><User size={11} />{execution.initiatedBy}</span>
            <span className="pb-meta-chip"><Clock size={11} />{formatDate(execution.startTime)}</span>
          </div>
        </div>

        <div className="pb-view-modal__stats">
          <div className="pb-view-stat">
            <span className="pb-view-stat__val">{execution.stepsCompleted}</span>
            <span className="pb-view-stat__lbl">Steps</span>
          </div>
          <div className="pb-view-stat">
            <span className="pb-view-stat__val">{execution.duration}</span>
            <span className="pb-view-stat__lbl">Duration</span>
          </div>
          <div className="pb-view-stat">
            <span className="pb-view-stat__val">{execution.initiatedBy}</span>
            <span className="pb-view-stat__lbl">Initiated By</span>
          </div>
        </div>

        {steps.length > 0 && (
          <div className="pb-view-modal__section">
            <h4 className="pb-view-modal__section-title">
              <Activity size={13} /> Step Results
            </h4>
            <div className="pb-view-steps">
              {steps.map((step, i) => {
                const done = i < completedCount;
                const failed = execution.status === 'failed' && i === completedCount;
                return (
                  <div
                    key={i}
                    className={`pb-view-step${done ? ' pb-view-step--done' : ''}${failed ? ' pb-view-step--failed' : ''}`}
                  >
                    <span className="pb-view-step__num">
                      {done ? <CheckCircle size={12} /> : failed ? <XCircle size={12} /> : i + 1}
                    </span>
                    <span className="pb-view-step__label">{step}</span>
                    {done && <span className="pb-view-step__check">✓</span>}
                    {failed && <span className="pb-view-step__fail">✗</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Templates Tab ────────────────────────────────────────────────────────────

function TemplatesTab({ onUseTemplate }) {
  const [search, setSearch] = useState('');

  const filtered = TEMPLATES.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="pb-tab-content">
      <div className="pb-toolbar">
        <div className="pb-search">
          <Search size={15} className="pb-search__icon" />
          <input
            type="text"
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pb-search__input"
          />
        </div>
        <div className="pb-toolbar__right">
          <button className="pb-btn pb-btn--ghost">
            <Filter size={14} />
            Filter
          </button>
        </div>
      </div>

      <div className="pb-templates-grid">
        {filtered.map((tpl) => {
          const Icon = tpl.icon;
          return (
            <div className="pb-template-card" key={tpl.id}>
              <div className="pb-template-card__header">
                <div
                  className="pb-template-card__icon"
                  style={{ '--icon-color': tpl.color }}
                >
                  <Icon size={20} />
                </div>
                <span className="pb-template-card__category">{tpl.category}</span>
              </div>

              <h3 className="pb-template-card__name">{tpl.name}</h3>
              <p className="pb-template-card__desc">{tpl.description}</p>

              <div className="pb-template-card__meta">
                <span className="pb-meta-chip">
                  <Zap size={12} />
                  {tpl.steps} steps
                </span>
                <span className="pb-meta-chip">
                  <Timer size={12} />
                  ~{tpl.avgDuration}
                </span>
              </div>

              <div className="pb-template-card__tags">
                {tpl.tags.map((tag) => (
                  <span key={tag} className="pb-tag">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="pb-template-card__actions">
                <button
                  className="pb-btn pb-btn--primary"
                  onClick={() => onUseTemplate && onUseTemplate(tpl)}
                >
                  <Play size={13} />
                  Use Template
                </button>
                <button className="pb-btn pb-btn--ghost pb-btn--icon" title="Preview">
                  <Eye size={14} />
                </button>
                <button className="pb-btn pb-btn--ghost pb-btn--icon" title="Duplicate">
                  <Copy size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── My Playbooks Tab ─────────────────────────────────────────────────────────

function MyPlaybooksTab() {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [playbooks, setPlaybooks] = useState(INITIAL_PLAYBOOKS);
  const [runModal, setRunModal] = useState(null);
  const [viewModal, setViewModal] = useState(null);
  const [editModal, setEditModal] = useState(null);

  const filters = ['all', 'active', 'draft', 'disabled'];

  const filtered = playbooks.filter((pb) => {
    const matchesFilter = filter === 'all' || pb.status === filter;
    const matchesSearch = pb.name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleSave = (updated) => {
    setPlaybooks((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  return (
    <div className="pb-tab-content">
      {runModal && (
        <RunModal playbook={runModal} onClose={() => setRunModal(null)} />
      )}
      {viewModal && (
        <ViewModal playbook={viewModal} onClose={() => setViewModal(null)} />
      )}
      {editModal && (
        <EditModal
          playbook={editModal}
          onSave={handleSave}
          onClose={() => setEditModal(null)}
        />
      )}

      <div className="pb-toolbar">
        <div className="pb-search">
          <Search size={15} className="pb-search__icon" />
          <input
            type="text"
            placeholder="Search playbooks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pb-search__input"
          />
        </div>
        <div className="pb-toolbar__right">
          <div className="pb-filter-group">
            {filters.map((f) => (
              <button
                key={f}
                className={`pb-filter-btn ${filter === f ? 'pb-filter-btn--active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <button className="pb-btn pb-btn--primary">
            <Plus size={14} />
            New Playbook
          </button>
        </div>
      </div>

      <div className="pb-playbook-list">
        {filtered.length === 0 && (
          <div className="pb-empty">
            <Activity size={32} />
            <p>No playbooks found</p>
          </div>
        )}
        {filtered.map((pb) => (
          <div className="pb-playbook-row" key={pb.id}>
            <div className="pb-playbook-row__main">
              <div className="pb-playbook-row__info">
                <div className="pb-playbook-row__name-row">
                  <span className="pb-playbook-row__name">{pb.name}</span>
                  <StatusBadge status={pb.status} />
                </div>
                <span className="pb-playbook-row__template">
                  <ChevronRight size={12} />
                  {pb.template}
                </span>
              </div>

              <div className="pb-playbook-row__stats">
                <div className="pb-stat">
                  <span className="pb-stat__label">Trigger</span>
                  <span className="pb-stat__value">{pb.trigger}</span>
                </div>
                <div className="pb-stat">
                  <span className="pb-stat__label">Last Run</span>
                  <span className="pb-stat__value">{formatDate(pb.lastRun)}</span>
                </div>
                <div className="pb-stat">
                  <span className="pb-stat__label">Total Runs</span>
                  <span className="pb-stat__value">{pb.totalRuns}</span>
                </div>
                <div className="pb-stat">
                  <span className="pb-stat__label">Success Rate</span>
                  <span className="pb-stat__value pb-stat__value--accent">
                    {pb.successRate !== null ? `${pb.successRate}%` : '—'}
                  </span>
                </div>
                {pb.nextRun && (
                  <div className="pb-stat">
                    <span className="pb-stat__label">Next Run</span>
                    <span className="pb-stat__value">
                      <Calendar size={11} style={{ marginRight: 3 }} />
                      {formatDate(pb.nextRun)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="pb-playbook-row__actions">
              {pb.status !== 'disabled' && (
                <button
                  className="pb-btn pb-btn--ghost pb-btn--sm"
                  title="Run now"
                  onClick={() => setRunModal(pb)}
                >
                  <Play size={13} />
                </button>
              )}
              <button
                className="pb-btn pb-btn--ghost pb-btn--sm"
                title="View"
                onClick={() => setViewModal(pb)}
              >
                <Eye size={13} />
              </button>
              <button
                className="pb-btn pb-btn--ghost pb-btn--sm"
                title="Edit"
                onClick={() => setEditModal(pb)}
              >
                <Edit3 size={13} />
              </button>
              <button className="pb-btn pb-btn--ghost pb-btn--sm pb-btn--danger" title="Delete">
                <Trash2 size={13} />
              </button>
              <button className="pb-btn pb-btn--ghost pb-btn--sm" title="More">
                <MoreVertical size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Execution History Tab ────────────────────────────────────────────────────

function ExecutionHistoryTab() {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [viewExec, setViewExec] = useState(null);

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'successful', label: 'Successful' },
    { key: 'failed', label: 'Failed' },
    { key: 'waiting_approval', label: 'Waiting for Approval' },
  ];

  const statusIcon = {
    successful: <CheckCircle size={14} className="exec-icon exec-icon--success" />,
    failed: <XCircle size={14} className="exec-icon exec-icon--error" />,
    waiting_approval: <AlertCircle size={14} className="exec-icon exec-icon--warning" />,
  };

  const filtered = EXECUTION_HISTORY.filter((ex) => {
    const matchesFilter = filter === 'all' || ex.status === filter;
    const matchesSearch = ex.playbookName.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="pb-tab-content">
      {viewExec && (
        <ExecViewModal execution={viewExec} onClose={() => setViewExec(null)} />
      )}

      <div className="pb-toolbar">
        <div className="pb-search">
          <Search size={15} className="pb-search__icon" />
          <input
            type="text"
            placeholder="Search executions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pb-search__input"
          />
        </div>
        <div className="pb-toolbar__right">
          <div className="pb-filter-group">
            {filters.map((f) => (
              <button
                key={f.key}
                className={`pb-filter-btn ${filter === f.key ? 'pb-filter-btn--active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="pb-exec-summary">
        <div className="pb-exec-stat-card pb-exec-stat-card--success">
          <CheckCircle size={18} />
          <div>
            <span className="pb-exec-stat-card__value">
              {EXECUTION_HISTORY.filter((e) => e.status === 'successful').length}
            </span>
            <span className="pb-exec-stat-card__label">Successful</span>
          </div>
        </div>
        <div className="pb-exec-stat-card pb-exec-stat-card--error">
          <XCircle size={18} />
          <div>
            <span className="pb-exec-stat-card__value">
              {EXECUTION_HISTORY.filter((e) => e.status === 'failed').length}
            </span>
            <span className="pb-exec-stat-card__label">Failed</span>
          </div>
        </div>
        <div className="pb-exec-stat-card pb-exec-stat-card--warning">
          <AlertCircle size={18} />
          <div>
            <span className="pb-exec-stat-card__value">
              {EXECUTION_HISTORY.filter((e) => e.status === 'waiting_approval').length}
            </span>
            <span className="pb-exec-stat-card__label">Waiting Approval</span>
          </div>
        </div>
        <div className="pb-exec-stat-card pb-exec-stat-card--info">
          <Activity size={18} />
          <div>
            <span className="pb-exec-stat-card__value">{EXECUTION_HISTORY.length}</span>
            <span className="pb-exec-stat-card__label">Total Executions</span>
          </div>
        </div>
      </div>

      <div className="pb-exec-table-wrap">
        <table className="pb-exec-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Playbook Name</th>
              <th>Trigger</th>
              <th>Initiated By</th>
              <th>
                <Clock size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Start Time
              </th>
              <th>Duration</th>
              <th>Steps</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="pb-exec-table__empty">
                  No executions found
                </td>
              </tr>
            )}
            {filtered.map((ex) => (
              <tr key={ex.id} className="pb-exec-table__row pb-exec-table__row--clickable" onClick={() => setViewExec(ex)}>
                <td>
                  <div className="pb-exec-status">
                    {statusIcon[ex.status]}
                    <StatusBadge status={ex.status} />
                  </div>
                </td>
                <td className="pb-exec-table__name">{ex.playbookName}</td>
                <td>
                  <span className="pb-meta-chip">{ex.trigger}</span>
                </td>
                <td className="pb-exec-table__by">{ex.initiatedBy}</td>
                <td className="pb-exec-table__time">{formatDate(ex.startTime)}</td>
                <td>
                  <span className="pb-exec-duration">{ex.duration}</span>
                </td>
                <td>
                  <span className="pb-exec-steps">{ex.stepsCompleted}</span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="pb-exec-table__actions">
                    <button
                      className="pb-btn pb-btn--ghost pb-btn--sm"
                      title="View details"
                      onClick={() => setViewExec(ex)}
                    >
                      <Eye size={13} />
                    </button>
                    {ex.status === 'waiting_approval' && (
                      <button className="pb-btn pb-btn--primary pb-btn--sm">
                        Approve
                      </button>
                    )}
                    {ex.status === 'failed' && (
                      <button className="pb-btn pb-btn--ghost pb-btn--sm" title="Retry">
                        <RefreshCw size={13} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'templates', label: 'Templates' },
  { key: 'my-playbooks', label: 'My Playbooks' },
  { key: 'execution-history', label: 'Execution History' },
];

export default function PlaybookAutomation() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = tab || 'templates';

  const handleTabChange = (key) => {
    navigate(`/playbook-automation/${key}`);
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'templates':
        return (
          <TemplatesTab
            onUseTemplate={() => navigate('/playbook-automation/my-playbooks')}
          />
        );
      case 'my-playbooks':
        return <MyPlaybooksTab />;
      case 'execution-history':
        return <ExecutionHistoryTab />;
      default:
        return <TemplatesTab />;
    }
  };

  return (
    <Box className="pb-page">
      <Box className="pb-page__header">
        <Box>
          <div className="pb-page__title-row">
            <Typography className="pb-page__title">Playbook Automation</Typography>
            <span className="pb-page__beta-tag">Beta</span>
          </div>
          <Typography className="pb-page__subtitle">
            Build, manage and monitor automated security response workflows
          </Typography>
        </Box>

        <div className="pb-header-stats">
          <div className="pb-header-stat">
            <span className="pb-header-stat__value">
              {INITIAL_PLAYBOOKS.filter((p) => p.status === 'active').length}
            </span>
            <span className="pb-header-stat__label">Active</span>
          </div>
          <div className="pb-header-stat">
            <span className="pb-header-stat__value">
              {EXECUTION_HISTORY.filter((e) => e.status === 'successful').length}
            </span>
            <span className="pb-header-stat__label">Runs Today</span>
          </div>
          <div className="pb-header-stat pb-header-stat--warn">
            <span className="pb-header-stat__value">
              {EXECUTION_HISTORY.filter((e) => e.status === 'waiting_approval').length}
            </span>
            <span className="pb-header-stat__label">Pending Approval</span>
          </div>
        </div>
      </Box>

      <div className="pb-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`pb-tab ${activeTab === t.key ? 'pb-tab--active' : ''}`}
            onClick={() => handleTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {renderTab()}
    </Box>
  );
}
